/**
 * Harms taxonomy editor: a list of prose blocks the model reads verbatim.
 *
 * Lifted out of ConfigRoute when per-device config landed, so the family
 * defaults page and a device's own override page edit a taxonomy through
 * exactly the same control. Fully controlled — the parent owns the value.
 */

import { PRESETS, type PresetName } from '../config/presets';
import type { HarmsTaxonomy } from '../data/config';
import { Button } from './Button';
import { Card } from './Card';

interface Props {
  value: HarmsTaxonomy;
  onChange: (next: HarmsTaxonomy) => void;
  /** Hide the preset row on the device page, where presets are a family concept. */
  showPresets?: boolean;
}

export function TaxonomyEditor({ value, onChange, showPresets = true }: Props) {
  function setCategories(categories: HarmsTaxonomy['categories']): void {
    onChange({ ...value, categories });
  }

  function updateAt(
    index: number,
    patch: Partial<HarmsTaxonomy['categories'][number]>,
  ): void {
    const cats = value.categories.slice();
    const c = cats[index];
    if (c === undefined) return;
    cats[index] = { ...c, ...patch };
    setCategories(cats);
  }

  function removeAt(index: number): void {
    const cats = value.categories.slice();
    cats.splice(index, 1);
    setCategories(cats);
  }

  function add(): void {
    setCategories([
      ...value.categories,
      {
        id: `custom-${String(Date.now())}`,
        label: 'New category',
        guidance:
          'Describe what to flag here. Include a clear "do NOT flag" clause so the model doesn\'t over-fire.',
      },
    ]);
  }

  function usePreset(name: PresetName): void {
    onChange(PRESETS[name]);
  }

  return (
    <div class="space-y-3">
      {showPresets ? (
        <div class="flex flex-wrap gap-2">
          {(['standard', 'strict', 'test-only'] as const).map((name) => (
            <Button
              key={name}
              variant="secondary"
              size="sm"
              onClick={() => {
                usePreset(name);
              }}
            >
              {name === 'test-only' ? 'Test-only (kittens)' : `${name} preset`}
            </Button>
          ))}
        </div>
      ) : null}

      {value.categories.map((c, i) => (
        <Card key={c.id} class="space-y-3">
          <div class="flex items-start justify-between gap-3">
            <label class="block flex-1 text-sm">
              <span class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted">
                Label
              </span>
              <input
                type="text"
                class="mt-1 h-9 w-full rounded border border-surface-muted/40 bg-surface px-2 text-[15px] font-medium"
                value={c.label}
                onInput={(e) => {
                  updateAt(i, { label: (e.target as HTMLInputElement).value });
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => {
                removeAt(i);
              }}
              class="mt-6 font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted transition-colors hover:text-signal"
            >
              Remove
            </button>
          </div>
          <label class="block text-sm">
            <span class="font-mono text-[11px] tracking-[0.14em] uppercase text-surface-muted">
              Guidance (fed to the AI verbatim)
            </span>
            <textarea
              class="mt-1 w-full rounded border border-surface-muted/40 bg-surface p-3 text-sm leading-relaxed font-normal"
              rows={5}
              placeholder="Flag when …&#10;Do NOT flag …"
              value={c.guidance}
              onInput={(e) => {
                updateAt(i, {
                  guidance: (e.target as HTMLTextAreaElement).value,
                });
              }}
            />
          </label>
        </Card>
      ))}

      <Button variant="secondary" size="sm" onClick={add}>
        + Add category
      </Button>
    </div>
  );
}
